/* Native controls remain available even when JavaScript or autoplay is unavailable. */
(() => {
  'use strict';
  if (window.nvtFilmPlayerInstalled) return;
  window.nvtFilmPlayerInstalled = true;
  const players = new WeakMap();
  const words = {
    en: { play: 'Play film', pause: 'Pause film', open: 'Open film' },
    ar: { play: 'تشغيل الفيديو', pause: 'إيقاف الفيديو', open: 'فتح الفيديو' },
    tr: { play: 'Filmi oynat', pause: 'Filmi duraklat', open: 'Filmi aç' },
    nl: { play: 'Film afspelen', pause: 'Film pauzeren', open: 'Film openen' }
  };
  function mount(root) {
    if (players.has(root)) return;
    const video = root.querySelector('video');
    const button = root.querySelector('[data-nvt-video-toggle]');
    const link = root.querySelector('[data-nvt-film-fallback]');
    if (!video) return;
    video.controls = true;
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const cleanup = [];
    let observer;
    let visible = false;
    let mode = 'auto';
    let policyPause = false;
    let autoRequested = false;
    let pending = false;
    let disposed = false;
    let failed = false;
    const on = (target, event, listener) => {
      target.addEventListener(event, listener);
      cleanup.push(() => target.removeEventListener(event, listener));
    };
    const label = () => words[(document.documentElement.lang || 'en').split('-')[0]] || words.en;
    function update() {
      if (button) {
        button.textContent = label()[video.paused ? 'play' : 'pause'];
        button.setAttribute('aria-pressed', String(!video.paused));
        button.hidden = failed;
      }
      if (link) { link.hidden = false; link.textContent = label().open; }
      root.classList.toggle('nvt-film--playing', !video.paused);
    }
    function pauseForVisibility() {
      if (!video.paused) { policyPause = true; video.pause(); }
    }
    function play(automatic) {
      if (disposed || pending || !video.paused || failed) return;
      autoRequested = automatic;
      if (automatic) video.muted = true;
      try {
        const attempt = video.play();
        if (attempt && typeof attempt.then === 'function') {
          pending = true;
          attempt.catch(() => {}).finally(() => {
            pending = false;
            autoRequested = false;
            if (!disposed) update();
          });
        }
      } catch (_) { autoRequested = false; update(); }
    }
    function reconcile() {
      if (disposed) return;
      const canAutoplay = !motion.matches && !navigator.connection?.saveData;
      if (!document.hidden && visible && mode !== 'paused' && (mode === 'manual' || canAutoplay)) play(mode === 'auto');
      else pauseForVisibility();
      update();
    }
    if (button) on(button, 'click', () => {
      if (video.paused) { mode = 'manual'; play(false); }
      else { mode = 'paused'; video.pause(); }
      update();
    });
    on(video, 'play', () => {
      if (!autoRequested) mode = 'manual';
      update();
    });
    on(video, 'playing', () => {
      if (document.hidden) pauseForVisibility();
      update();
    });
    on(video, 'pause', () => {
      if (policyPause) policyPause = false;
      else mode = 'paused';
      update();
    });
    on(video, 'error', () => { failed = true; update(); });
    on(document, 'noveino-language-changed', update);
    on(document, 'visibilitychange', reconcile);
    if (motion.addEventListener) on(motion, 'change', reconcile);
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .08;
        reconcile();
      }, { threshold: [0, .08] });
      observer.observe(video);
    }
    if (motion.matches || navigator.connection?.saveData) video.preload = 'none';
    players.set(root, () => {
      disposed = true;
      observer?.disconnect();
      cleanup.forEach(fn => fn());
      video.pause();
      players.delete(root);
    });
    update();
  }
  function each(scope, callback) {
    if (scope.matches?.('[data-nvt-film-player]')) callback(scope);
    scope.querySelectorAll('[data-nvt-film-player]').forEach(callback);
  }
  const start = () => each(document, mount);
  document.addEventListener('shopify:section:load', event => each(event.target, mount));
  document.addEventListener('shopify:section:unload', event => each(event.target, root => players.get(root)?.()));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
